import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';
import Papa from 'papaparse';
import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { TOAST_ERROR, TOAST_WARNING } from '../../../utils/constants';
import { Flex, Icon, Modal, theme, Toggler, Typo, SimpleButton } from '../../../ui';
import PageControls from '../../../ui/Page/PageControls';
import { useEditableCourses } from '../../courses/hooks';
import { useReaders } from '../../users/hooks/useUsersState';
import { useUsersActions } from '../../users/hooks/useUsersActions';
import { useEditableGroups } from '../../groups/hooks/useGroupsState';
import { useGroupsActions } from '../../groups/hooks/useGroupsActions';
import { useCourseDurationsState } from '../../course/hooks';
import { useInvitesModalState, useIsSendingInvitations } from '../hooks/useInvitesState';
import { useInvitesActions } from '../hooks/useInvitesActions';
import UcpPeriodSelector from '../../../components/UcpPeriodSelector';
import SearchSelect from '../../../ui/SearchSelect';
import Buttons from '../../../ui/Buttons';
import FileUploader from '../../media/uploaders/FileUploader';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;


const SendInvite = () => {
  const { t } = useTranslation();

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isGroup, setIsGroup] = useState(false);
  const [duration, setDuration] = useState(null);
  const [contentValid, setContentValid] = useState(false);
  const [searchText, setSearchText] = useState('');

  const courseDurations = useCourseDurationsState();
  const { modalState } = useInvitesModalState();
  const isModalOpen = modalState?.isInviteModalOpen || false;
  const modalProps = modalState?.modalProps || {};

  const { courses = [] } = useEditableCourses({ searchText: '', showUncorrected: false }, { skip: !isModalOpen });

  const hasSearchText = Boolean(searchText && searchText.trim().length >= 2);

  const {
    readers: users = [],
    isLoading,
    isFetching,
  } = useReaders(
    {
      pageLength: 50,
      ...(hasSearchText && { searchText: searchText.trim() }),
      sortBy: 'name',
    },
    { skip: !isModalOpen },
  );

  const isLoadingUsers = isLoading || isFetching;

  const { addUserToGroup } = useUsersActions();
  const { sendInvitations, closeInvitesModal } = useInvitesActions();
  const { addGroupToCourse } = useGroupsActions();
  const { groups } = useEditableGroups({ disableCounts: true }, { skip: !isModalOpen });
  const isSendingInvitations = useIsSendingInvitations();

  const { group, course, select } = modalProps;

  useEffect(() => {
    let isValid = true;
    const hasUCP = duration?.hasOwnProperty('id');
    if (!group && !course) {
      isValid = !!hasUCP && !!selectedCourses.length && !!(selectedGroups.length || selectedUsers.length);
    } else if (select === 'courses') {
      isValid = !!hasUCP && !!selectedCourses.length;
    } else {
      isValid = selectedUsers.length;
    }

    setContentValid(isValid);
  }, [duration, selectedGroups, selectedCourses, select, selectedUsers, course, group]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue) => {
        setSearchText(searchValue);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = useCallback(
    (value) => {
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  const handleUsersChange = useCallback(
    (newUsers) => {
      setSelectedUsers(newUsers);
      // Clear search text and cancel pending debounced search when user makes a selection
      setSearchText('');
      debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  const handleGroupOrUser = useCallback((checked) => {
    setIsGroup(checked);
  }, []);

  const clearState = useCallback(() => {
    setSelectedGroups([]);
    setSelectedCourses([]);
    setSelectedUsers([]);
    setIsGroup(false);
    setDuration(null);
    setContentValid(false);
    setSearchText('');
  }, []);

  const closeDialog = useCallback(() => {
    debouncedSearch.cancel();
    closeInvitesModal();
    clearState();
  }, [closeInvitesModal, clearState, debouncedSearch]);

  const sendInvitation = useCallback(async () => {
    const expires = !!duration && duration.id !== 0 ? dayjs().add(duration?.id, 'month').format('YYYY-MM-DD') : '0000-00-00';
    const courseIds = selectedCourses?.map((course) => course.id);
    const groupsById = selectedGroups.map((group) => group.id);
    const usersById = selectedUsers.map((user) => user.id).filter(Boolean);
    const emails = selectedUsers
      .map((user) => {
        const email = user.inputValue || (!user.id && (user.email || user));
        return email ? email.toLowerCase().trim() : null;
      })
      .filter(Boolean);

    if (group?.id) {
      if (select === 'users' && usersById.length) {
        await addUserToGroup({ userId: usersById, circleId: group?.id });
      }
      if (select === 'courses' && courseIds.length) {
        await addGroupToCourse({ ucpPeriod: expires, circleId: group.id, courseIds: courseIds });
      }
      if (emails.length) {
        await sendInvitations({ expires, byWhat: 'emails', withWhat: emails, circleId: group.id });
      }
    } else {
      const coursesToBeInvited = course?.id ? [course?.id] : courseIds;
      if (groupsById.length && isGroup) {
        await sendInvitations({ expires, byWhat: 'groups', withWhat: groupsById, courseIds: coursesToBeInvited });
      }
      if (usersById.length && !isGroup) {
        await sendInvitations({ expires, byWhat: 'users', withWhat: usersById, courseIds: coursesToBeInvited });
      }
      if (emails.length) {
        await sendInvitations({ expires, byWhat: 'emails', withWhat: emails, courseIds: coursesToBeInvited });
      }
    }

    closeDialog();
  }, [
    duration,
    selectedGroups,
    selectedCourses,
    select,
    selectedUsers,
    isGroup,
    closeDialog,
    course,
    group,
    sendInvitations,
    addUserToGroup,
    addGroupToCourse,
  ]);

  const changeHandler = useCallback(
    (file) => {
      if (file) {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          transformHeader: function (header) {
            return header.toLocaleLowerCase();
          },
          complete: function (results) {
            const selectedUsersList = [];
            let invalidRowCount = 0;

            if (results.data.length) {
              results.data.forEach((user) => {
                // Skip rows without email property or with empty email
                if (!user.email || typeof user.email !== 'string') {
                  invalidRowCount++;
                  return;
                }

                const email = user.email.trim().toLowerCase();
                if (!email) {
                  invalidRowCount++;
                  return;
                }

                selectedUsersList.push({
                  email: email,
                  name: user.hasOwnProperty('name') && user.name ? user.name.trim() : email,
                });
              });
            }

            if (selectedUsersList.length > 0) {
              setSelectedUsers(selectedUsersList);

              // Show warning if some rows were invalid
              if (invalidRowCount > 0) {
                enqueueSnackbar(t('invites.csvPartialImport', { valid: selectedUsersList.length, invalid: invalidRowCount }), { variant: TOAST_WARNING });
              }
            } else {
              // No valid users found
              if (results.data.length > 0) {
                enqueueSnackbar(t('invites.csvInvalidFormat'), { variant: TOAST_ERROR });
              } else {
                enqueueSnackbar(t('invites.csvEmpty'), { variant: TOAST_ERROR });
              }
            }
          },
          error: function (error) {
            enqueueSnackbar(t('invites.csvParseError'), { variant: TOAST_ERROR });
          },
        });
      }
    },
    [t],
  );

  return (
    <Modal open={isModalOpen} onClose={closeDialog} width={'525px'} title={t('invites.sentTitle')}>
      <Wrapper>
        {course?.id && (
          <div>
            <Flex gap='10'>
              <Icon.Course color={theme.colors.NEU_600} />
              <Typo.TextTitle>{t('invites.invCourse', { courseName: course.name })}</Typo.TextTitle>
            </Flex>
          </div>
        )}
        {!!group?.id ? (
          <div>
            <Flex gap='10'>
              <Icon.Groups />
              <Typo.TextTitle>{t('invites.invGroup', { groupName: group.name })}</Typo.TextTitle>
            </Flex>
          </div>
        ) : (
          <div>
            <Typo.TextTitle>{t('invites.invUserOrGroup')}</Typo.TextTitle>
            <Toggler leftText={t('globals.users')} rightText={t('globals.groups')} onClick={handleGroupOrUser} />
          </div>
        )}
        {isGroup ? (
          <div>
            <Typo.TextTitle>{t('invites.selectGroups')}</Typo.TextTitle>
            <SearchSelect
              multiple={true}
              setValue={setSelectedGroups}
              selectedOptions={selectedGroups}
              id='groups'
              options={groups.map((item) => ({ name: item.name, id: item.id }))}
            />
          </div>
        ) : (
          select !== 'courses' && (
            <div>
              <Typo.TextTitle>{t('invites.addUsers')}</Typo.TextTitle>
              <SearchSelect
                creatable
                optionType={'email'}
                multiple={true}
                setValue={handleUsersChange}
                selectedOptions={selectedUsers}
                id='users'
                options={users}
                label={t('invites.addUserIfDoesntExist')}
                onSearchChange={handleSearchChange}
                loading={isLoadingUsers}
                disableLocalFilter={true}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.id || option}>
                      <div>
                        <Typo.Text>{option.name}</Typo.Text>
                        <Typo.Info>{option.email}</Typo.Info>
                      </div>
                    </li>
                  );
                }}
              />
              <Flex gap='10'>
                <Typo.SmallText $marginBottom='5px'>
                  {t('invites.invCsv')} {t('invites.csvInstruction')}
                </Typo.SmallText>
                <FileUploader
                  accept='.csv'
                  id={'inviteUploadfile'}
                  saveAction={changeHandler}
                  label={t('invites.csvUpload')}
                  isBlobToReturn={true}
                  noMediaLibrary
                />
              </Flex>
            </div>
          )
        )}
        {select !== 'users' && !course && (
          <div>
            <Typo.TextTitle>{t('invites.selectCourses')}</Typo.TextTitle>
            <SearchSelect
              multiple={true}
              setValue={setSelectedCourses}
              selectedOptions={selectedCourses}
              id='courses'
              label={t('invites.courseInstruction')}
              options={courses.map((item) => ({ name: `${item.name} (${item.id})`, id: item.id }))}
            />
          </div>
        )}
        {/* this should be presented with course is present not for ading users to groups */}
        {!(select === 'users' && group?.id) && (
          <div>
            <UcpPeriodSelector setDuration={setDuration} duration={duration} options={courseDurations} />
          </div>
        )}
      </Wrapper>

      <PageControls
        openModelButton={
          <Flex gap='10'>
            {!isSendingInvitations && <Buttons.Cancel onClick={closeDialog} />}
            <SimpleButton onClick={sendInvitation} isLoading={isSendingInvitations} disabled={!contentValid}>
              {t('globals.send')}
            </SimpleButton>
          </Flex>
        }
      />
    </Modal>
  );
};

export default SendInvite;
